import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import useAuthStore from '@/store/authStore';
import {
   clientSignInApi,
   type ClientProfile,
} from '@/api/clientPortal';
import ClientPortalHome from './Home';

/**
 * Basic portal (client.mysmartfi.ph) — for a buyer who received a SUB
 * LOGIN instead of a full transfer. Deliberately tiny: one device,
 * simple words, big inputs. Login = seller's email + username +
 * password (buyers in remote areas often have no email of their own).
 */

const PROFILE_KEY = 'x-client-profile';

export function loadClientProfile(): ClientProfile | null {
   try {
      const raw = localStorage.getItem(PROFILE_KEY);
      return raw ? (JSON.parse(raw) as ClientProfile) : null;
   } catch {
      return null;
   }
}

export function clearClientSession() {
   try {
      localStorage.removeItem(PROFILE_KEY);
   } catch {
      /* ignore */
   }
   useAuthStore.getState().clearAuth();
}

const ClientPortalApp: React.FC = () => {
   const { token } = useAuthStore();
   const [profile, setProfile] = useState<ClientProfile | null>(() =>
      loadClientProfile()
   );

   if (token && profile) {
      return (
         <ClientPortalHome
            profile={profile}
            onLogout={() => {
               clearClientSession();
               setProfile(null);
            }}
         />
      );
   }
   return (
      <Login
         onSignedIn={(p) => {
            setProfile(p);
         }}
      />
   );
};

const Login: React.FC<{ onSignedIn: (p: ClientProfile) => void }> = ({
   onSignedIn,
}) => {
   const [sellerEmail, setSellerEmail] = useState('');
   const [username, setUsername] = useState('');
   const [password, setPassword] = useState('');
   const [fieldErr, setFieldErr] = useState<Record<string, string>>({});
   const [busy, setBusy] = useState(false);

   const submit = async (e: React.FormEvent) => {
      e.preventDefault();
      const errs: Record<string, string> = {};
      if (!sellerEmail.trim()) errs.sellerEmail = 'Type the seller’s email';
      if (!username.trim()) errs.username = 'Type your username';
      if (!password) errs.password = 'Type your password';
      setFieldErr(errs);
      if (Object.keys(errs).length) return;
      setBusy(true);
      try {
         const res = await clientSignInApi({
            sellerEmail: sellerEmail.trim(),
            username: username.trim(),
            password,
         });
         localStorage.setItem(PROFILE_KEY, JSON.stringify(res.client));
         useAuthStore
            .getState()
            .setAuth(res.token, {
               name: res.client.username,
               email: res.client.sellerEmail,
            });
         onSignedIn(res.client);
      } catch (err: any) {
         toast.error(
            err?.response?.data?.err ||
               err?.response?.data?.error ||
               'Login failed — check the details and try again'
         );
      } finally {
         setBusy(false);
      }
   };

   return (
      <Page>
         <LoginCard onSubmit={submit}>
            <Brand>SmartFi</Brand>
            <Title>My Vendo Portal</Title>
            <Sub>
               Sign in with the details given to you by the seller of your
               vendo machine.
            </Sub>

            <Field>
               <Label>Seller’s email</Label>
               <Input
                  type="email"
                  inputMode="email"
                  autoCapitalize="none"
                  placeholder="seller@email.com"
                  value={sellerEmail}
                  onChange={(e) => setSellerEmail(e.target.value)}
               />
               {fieldErr.sellerEmail && <FieldError>{fieldErr.sellerEmail}</FieldError>}
            </Field>
            <Field>
               <Label>Username</Label>
               <Input
                  autoCapitalize="none"
                  placeholder="my-vendo-x4t9"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
               />
               {fieldErr.username && <FieldError>{fieldErr.username}</FieldError>}
            </Field>
            <Field>
               <Label>Password</Label>
               <Input
                  type="password"
                  placeholder="mango-tiger-42"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
               />
               {fieldErr.password && <FieldError>{fieldErr.password}</FieldError>}
            </Field>

            <SignInBtn type="submit" disabled={busy}>
               {busy ? 'Signing in…' : 'Sign in'}
            </SignInBtn>
            <Help>
               Lost your password? Ask the seller — they can give you a new
               one anytime.
            </Help>
         </LoginCard>
      </Page>
   );
};

export default ClientPortalApp;

const Page = styled.div`
   min-height: 100dvh;
   display: flex;
   align-items: center;
   justify-content: center;
   padding: 20px;
   background: var(--app-bg, #f4f6fa);
`;

const LoginCard = styled.form`
   width: 100%;
   max-width: 380px;
   background: var(--app-surface, #fff);
   border: 1px solid var(--app-border, #e3e8ef);
   border-radius: 16px;
   padding: 26px 22px;
`;

const Brand = styled.div`
   font-size: 13px;
   font-weight: 800;
   letter-spacing: 1px;
   text-transform: uppercase;
   color: var(--app-text-muted, #7b8794);
`;

const Title = styled.h1`
   margin-top: 4px;
   font-size: 22px;
   font-weight: 800;
`;

const Sub = styled.p`
   margin-top: 6px;
   font-size: 13px;
   color: var(--app-text-dim, #55606c);
   line-height: 1.5;
`;

const Field = styled.div`
   margin-top: 14px;
`;

const Label = styled.label`
   display: block;
   font-size: 12px;
   font-weight: 700;
   margin-bottom: 5px;
`;

const Input = styled.input`
   width: 100%;
   padding: 12px 13px;
   border-radius: 10px;
   border: 1.5px solid var(--app-border, #e3e8ef);
   font-size: 15px;
   &:focus {
      outline: none;
      border-color: #4f7cff;
   }
`;

const FieldError = styled.div`
   margin-top: 4px;
   font-size: 11.5px;
   color: #dc3545;
   font-weight: 600;
`;

const SignInBtn = styled.button`
   margin-top: 18px;
   width: 100%;
   border: none;
   background: #4f7cff;
   color: #fff;
   border-radius: 10px;
   padding: 13px;
   font-size: 15px;
   font-weight: 700;
   cursor: pointer;
   &:disabled {
      opacity: 0.6;
   }
`;

const Help = styled.p`
   margin-top: 14px;
   font-size: 12px;
   color: var(--app-text-muted, #7b8794);
   text-align: center;
   line-height: 1.5;
`;
